import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

/**
 * ActivityHeartbeat
 *
 * - Listens to mouse / keyboard / scroll / touch events globally.
 * - Counts seconds the user is "active" (interacted within the last 30s tick).
 * - Every 60 seconds, POSTs the accumulated active/idle deltas to
 *   `/api/activity/heartbeat`. The backend aggregates per-user per-day.
 * - Pauses while the tab is hidden (visibilitychange='hidden') — those
 *   seconds count as idle.
 * - Stops cleanly on unmount or logout.
 *
 * No UI. Mounted once inside Layout.
 */
const TICK_SECONDS = 60;
const ACTIVE_WINDOW_MS = 30_000;

export default function ActivityHeartbeat() {
  const { user } = useAuth();
  const lastInteractionRef = useRef(0);
  const accActiveRef = useRef(0);
  const accIdleRef = useRef(0);
  const lastTickAtRef = useRef(Date.now());

  useEffect(() => {
    if (!user) return undefined;
    // Skip temp / candidate roles — they don't need activity tracking.
    if (user.role === "candidate_temp" || user.role === "ex_employee_temp") return undefined;

    const bump = () => { lastInteractionRef.current = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "touchmove"];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));

    const tick = () => {
      const now = Date.now();
      const elapsedMs = now - lastTickAtRef.current;
      lastTickAtRef.current = now;
      const elapsedSec = Math.round(elapsedMs / 1000);
      const wasActive = (now - lastInteractionRef.current) < ACTIVE_WINDOW_MS;
      if (document.visibilityState === "hidden" || !wasActive) {
        accIdleRef.current += elapsedSec;
      } else {
        accActiveRef.current += elapsedSec;
      }
    };
    const flush = async () => {
      const active = accActiveRef.current;
      const idle = accIdleRef.current;
      if (active === 0 && idle === 0) return;
      accActiveRef.current = 0;
      accIdleRef.current = 0;
      try {
        await api.post("/activity/heartbeat", {
          active_seconds: active,
          idle_seconds: idle,
          page: (typeof window !== "undefined" ? window.location.pathname : "") || "",
        });
      } catch { /* silent — heartbeat is best-effort */ }
    };

    // Tick every second to track elapsed time accurately; flush every TICK_SECONDS.
    const tickT = setInterval(tick, 1000);
    const flushT = setInterval(flush, TICK_SECONDS * 1000);

    // Beacon flush on unload so we don't lose the last partial tick.
    const onUnload = () => {
      try {
        const active = accActiveRef.current;
        const idle = accIdleRef.current;
        if (active === 0 && idle === 0) return;
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/activity/heartbeat`;
        const body = JSON.stringify({ active_seconds: active, idle_seconds: idle, page: window.location.pathname });
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
      } catch { /* */ }
    };
    window.addEventListener("beforeunload", onUnload);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bump));
      window.removeEventListener("beforeunload", onUnload);
      clearInterval(tickT);
      clearInterval(flushT);
      // Final flush
      flush();
    };
  }, [user]);

  return null;
}
